/**
 * Localized labels of the recipient composites (RecipientInput, AddressBookDialog) from
 * @platform/i18n (`webmail.recipients.*`, `webmail.addressBook.*`).
 */
import { useI18n } from '@platform/i18n';
import type { AddressBookLabels, RecipientInputLabels, RecipientSource } from '@platform/ui';
import { useMemo } from 'react';

export function useRecipientLabels(): { recipientLabels: RecipientInputLabels; addressBookLabels: AddressBookLabels } {
  const { t } = useI18n();
  return useMemo(() => {
    const sources = (prefix: 'webmail.recipients.sources' | 'webmail.addressBook.sources'): Record<RecipientSource, string> => ({
      recent: t(`${prefix}.recent`),
      contact: t(`${prefix}.contact`),
      gal: t(`${prefix}.gal`),
      external: t(`${prefix}.external`),
    });
    return {
      recipientLabels: {
        suggestions: t('webmail.recipients.suggestions'),
        searching: t('webmail.recipients.searching'),
        noMatches: t('webmail.recipients.noMatches'),
        remove: t('webmail.recipients.remove'),
        removeSuggestion: t('webmail.recipients.removeSuggestion'),
        expandGroup: t('webmail.recipients.expandGroup'),
        unresolved: t('webmail.recipients.unresolved'),
        ambiguous: t('webmail.recipients.ambiguous'),
        invalid: t('webmail.recipients.invalid'),
        pending: t('webmail.recipients.pending'),
        chooseCandidate: t('webmail.recipients.chooseCandidate'),
        group: t('webmail.recipients.group'),
        sources: sources('webmail.recipients.sources'),
      },
      addressBookLabels: {
        title: t('webmail.addressBook.title'),
        description: t('webmail.addressBook.description'),
        search: t('webmail.addressBook.search'),
        filters: { all: t('webmail.addressBook.filters.all'), gal: t('webmail.addressBook.filters.gal'), contact: t('webmail.addressBook.filters.contact') },
        sources: sources('webmail.addressBook.sources'),
        group: t('webmail.addressBook.group'),
        results: t('webmail.addressBook.results'),
        empty: t('webmail.addressBook.empty'),
        loadMore: t('webmail.addressBook.loadMore'),
        addTo: t('webmail.addressBook.addTo'),
        remove: t('webmail.addressBook.remove'),
        apply: t('webmail.addressBook.apply'),
        cancel: t('webmail.addressBook.cancel'),
      },
    };
  }, [t]);
}
